import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Award, Target, Users, BookOpen, TrendingUp, Heart, AlertCircle, CheckCircle, ArrowRight, ExternalLink, Copy, Calendar } from 'lucide-react';

// Appointment Setting Scripts from Playbook
const appointmentScripts = [
  {
    id: 'option1',
    title: 'Appointment Setting - Option 1 (Direct)',
    content: `Hi [Name], I'm setting up appointments this week to go over some financial strategies. Would Tuesday evening or Wednesday afternoon work better for you?`,
    tip: 'Best for: Warm market contacts who already know and trust you.'
  },
  {
    id: 'option2', 
    title: 'Appointment Setting - Option 2 (Soft)',
    content: `Hey [Name], I'm helping families understand their options for protecting their income and reaching financial goals. Can we grab coffee this week? I'd love to share what I've learned.`,
    tip: 'Best for: Close friends and family members you want to approach casually.'
  }
];

const reasonOptions = [
  "Don't like current job",
  "Want extra part-time income",
  "Want financial education",
  "Love helping people",
  "Want my own business"
];

const milestones = [
  { id: 'district_leader', label: 'District Leader Promotion', icon: Target },
  { id: 'first_division', label: 'Promote 1st District Leader (Division Leader)', icon: Users },
  { id: 'power_builder_watch', label: 'Power Builder Watch ($50,000)', icon: Award },
  { id: 'regional_leader', label: 'Regional Leader Promotion', icon: TrendingUp },
  { id: 'power_builder_ring', label: 'Power Builder Ring ($100,000)', icon: Award },
  { id: 'rvp', label: 'Regional Vice President Promotion', icon: TrendingUp },
  { id: 'ownership', label: 'Ownership ($300,000)', icon: Award },
  { id: 'million_dollar_ring', label: 'Million Dollar Ring ($1,000,000)', icon: Award }
];

const scripts = [
  {
    title: 'Training Appointment Option 1',
    content: 'Hi [Name], I\'m setting up appointments this week to go over some financial strategies. Would Tuesday evening or Wednesday afternoon work better for you?'
  },
  {
    title: 'Training Appointment Option 2',
    content: 'Hey [Name], I\'m helping families understand their options for protecting their income and reaching financial goals. Can we grab coffee this week? I\'d love to share what I\'ve learned.'
  },
  {
    title: 'Opportunity Meeting Invite',
    content: 'Hi [Name], we\'re having a business overview meeting this [Day] at [Time]. It\'s about how people are building extra income and helping families. Would you like to come check it out with me?'
  }
];

const terminology = [
  { term: 'FNA', definition: 'Financial Needs Analysis' },
  { term: 'IBA', definition: 'Independent Business Application' },
  { term: 'BP/BPP', definition: 'Base Premium / Base Premium Production' },
  { term: 'Sprint to District', definition: '3 Recruits + 3 Life Apps + $3,000 BPP in 30 days' },
  { term: 'FIN', definition: 'Financial Independence Number' },
  { term: 'Warm Market', definition: 'People you already know (friends, family, contacts)' },
  { term: 'Cold Market', definition: 'People you don\'t know yet (networking, social media)' },
  { term: 'Opp Meeting', definition: 'Opportunity Meeting - Business overview presentation' }
];

export const FastStartPlanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [planner, setPlanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prospects, setProspects] = useState([]);

  const promotionRequirements = {
    'Rep': {
      nextRole: 'District Leader',
      requirements: [
        'Complete Sprint to District (3 Recruits + 3 Apps + $3,000 BP)',
        'Pass LLQP License Exam',
        'Maintain active status'
      ],
      icon: Target
    },
    'District_Leader': {
      nextRole: 'Division Leader',
      requirements: [
        'Promote your first District Leader',
        'Build and train your team',
        'Consistent production'
      ],
      icon: Users
    },
    'Division_Leader': {
      nextRole: 'Regional Leader',
      requirements: [
        'Develop multiple District Leaders',
        'Build leadership pipeline',
        'Achieve production targets'
      ],
      icon: TrendingUp
    },
    'Regional_Leader': {
      nextRole: 'Senior Regional Leader',
      requirements: [
        'Develop Division Leaders',
        'Expand regional team',
        'Hit higher production levels'
      ],
      icon: Award
    },
    'Senior_Regional_Leader': {
      nextRole: 'Regional Vice President',
      requirements: [
        'Build multiple Regional Leaders',
        'Achieve RVP production requirements',
        'Demonstrate leadership excellence'
      ],
      icon: Award
    },
    'Regional_Vice_President': {
      nextRole: 'Ownership Level',
      requirements: [
        'Achieve $300,000+ in production',
        'Build sustainable organization',
        'Work toward Million Dollar Ring'
      ],
      icon: Award
    }
  };

  const fetchPlanner = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/planner');
      setPlanner(response.data);
    } catch (error) {
      toast.error('Failed to load planner');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProspects = useCallback(async () => {
    try {
      const response = await api.get('/prospects');
      setProspects(response.data);
    } catch (error) {
      // Silent fail - prospects list will be empty
    }
  }, []);

  useEffect(() => {
    fetchPlanner();
    fetchProspects();
  }, [fetchPlanner, fetchProspects]);

  const findProspectByName = (name) => {
    if (!name) return null;
    const normalizedName = name.toLowerCase().trim();
    return prospects.find(p => p.name.toLowerCase().trim() === normalizedName);
  };

  const navigateToProspect = (name) => {
    const prospect = findProspectByName(name);
    if (prospect) {
      navigate(`/pipeline?search=${encodeURIComponent(name)}`);
    } else {
      toast.info('Prospect not found in pipeline. Navigate to add them?');
      navigate('/pipeline');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/planner', planner);
      toast.success('Planner saved successfully!');
    } catch (error) {
      toast.error('Failed to save planner');
    } finally {
      setSaving(false);
    }
  };

  const toggleReason = (reason) => {
    if (!planner) return;
    const reasons = planner.reasons || [];
    const updated = reasons.includes(reason)
      ? reasons.filter(r => r !== reason)
      : [...reasons, reason];
    setPlanner({ ...planner, reasons: updated });
  };

  const updateArrayField = (field, index, value) => {
    if (!planner) return;
    const arr = [...(planner[field] || [])];
    arr[index] = value;
    setPlanner({ ...planner, [field]: arr });
  };

  const updateMilestone = (milestoneId, dateType, value) => {
    if (!planner) return;
    const milestones = planner.milestones || [];
    const existing = milestones.find(m => m.id === milestoneId);
    
    if (existing) {
      const updated = milestones.map(m =>
        m.id === milestoneId ? { ...m, [dateType]: value } : m
      );
      setPlanner({ ...planner, milestones: updated });
    } else {
      setPlanner({
        ...planner,
        milestones: [...milestones, { id: milestoneId, [dateType]: value }]
      });
    }
  };

  const getMilestoneDate = (milestoneId, dateType) => {
    if (!planner?.milestones) return '';
    const milestone = planner.milestones.find(m => m.id === milestoneId);
    return milestone?.[dateType] || '';
  };

  const updateTopContact = (index, field, value) => {
    if (!planner) return;
    const contacts = [...(planner.top_10_contacts || [])];
    if (!contacts[index]) {
      contacts[index] = {};
    }
    contacts[index] = { ...contacts[index], [field]: value };
    setPlanner({ ...planner, top_10_contacts: contacts });
  };

  const getTopContact = (index, field) => {
    return planner?.top_10_contacts?.[index]?.[field] || '';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20">Loading...</div>;
  }

  if (!planner) return null;

  const sprintFilled = (() => {
    const tracker = planner.sprint_tracker || {};
    const recruits = [tracker.recruit1, tracker.recruit2, tracker.recruit3].filter(Boolean).length;
    const apps = [tracker.app1, tracker.app2, tracker.app3].filter(Boolean).length;
    return recruits === 3 && apps === 3;
  })();

  return (
    <div className="space-y-6 pb-20" data-testid="fast-start-planner-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#333333]">Fast Start Planner</h1>
        <Button
          onClick={handleSave}
          className="bg-[#E31837] hover:bg-[#C4122C]"
          disabled={saving}
          data-testid="save-planner-button"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Promotion Roadmap */}
      {user && promotionRequirements[user.role] && (
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-[#E31837]">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="h-8 w-8 text-[#E31837]" />
            <div>
              <h2 className="text-2xl font-bold text-[#333333]">Your Next Promotion Goal</h2>
              <p className="text-gray-600">Current Role: <Badge className="ml-2">{user.role.replace('_', ' ')}</Badge></p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6 p-4 bg-white rounded-lg shadow-sm">
            <div className="text-center flex-1">
              <div className="text-lg font-bold text-[#333333]">{user.role.replace('_', ' ')}</div>
              <div className="text-sm text-gray-500">Current</div>
            </div>
            
            <ArrowRight className="h-8 w-8 text-[#E31837] mx-4" />
            
            <div className="text-center flex-1">
              <div className="text-lg font-bold text-[#E31837]">{promotionRequirements[user.role].nextRole}</div>
              <div className="text-sm text-gray-500">Next Level</div>
            </div>
          </div>

          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-[#E31837]" />
            Requirements to Reach {promotionRequirements[user.role].nextRole}:
          </h3>
          <div className="space-y-2">
            {promotionRequirements[user.role].requirements.map((req, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                <div className="h-6 w-6 rounded-full bg-[#E31837] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {idx + 1}
                </div>
                <p className="text-[#333333] flex-1">{req}</p>
              </div>
            ))}
          </div>

          {user.role === 'Rep' && sprintFilled && planner.llqp_license && (
            <div className="mt-4 p-4 bg-green-100 border-2 border-green-600 rounded-lg">
              <p className="text-green-800 font-bold text-center flex items-center justify-center gap-2">
                <Award className="h-6 w-6" />
                🎉 Congratulations! You've completed all requirements for District Leader promotion!
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Section A: 5 Reasons */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="h-6 w-6 text-[#E31837]" />
          <h2 className="text-2xl font-bold text-[#E31837]">5 Reasons I'm Here</h2>
        </div>
        <div className="space-y-3">
          {reasonOptions.map((reason, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <Checkbox
                id={`reason-${idx}`}
                checked={planner.reasons?.includes(reason)}
                onCheckedChange={() => toggleReason(reason)}
                data-testid={`reason-checkbox-${idx}`}
              />
              <label htmlFor={`reason-${idx}`} className="text-sm cursor-pointer">
                {reason}
              </label>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <Label htmlFor="personal-reason">My Personal Reason</Label>
          <Textarea
            id="personal-reason"
            value={planner.personal_reason || ''}
            onChange={(e) => setPlanner({ ...planner, personal_reason: e.target.value })}
            rows={3}
            placeholder="Write your personal reason here..."
            data-testid="personal-reason-input"
          />
        </div>
      </Card>

      {/* Section B: Dreams & Don't Wants */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-xl font-bold text-[#E31837] mb-4">Top 5 Dreams</h2>
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map(idx => (
              <Input
                key={idx}
                placeholder={`Dream #${idx + 1}`}
                value={planner.dreams?.[idx] || ''}
                onChange={(e) => updateArrayField('dreams', idx, e.target.value)}
                data-testid={`dream-input-${idx}`}
              />
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-bold text-[#E31837] mb-4">Top 5 Things I Don't Want</h2>
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map(idx => (
              <Input
                key={idx}
                placeholder={`Don't Want #${idx + 1}`}
                value={planner.dont_wants?.[idx] || ''}
                onChange={(e) => updateArrayField('dont_wants', idx, e.target.value)}
                data-testid={`dont-want-input-${idx}`}
              />
            ))}
          </div>
        </Card>
      </div>

      {/* Section C: FNA Numbers */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-[#E31837] mb-4">FNA Numbers</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="fin">Financial Independence Number</Label>
            <Input
              id="fin"
              type="number"
              placeholder="$"
              value={planner.fna_numbers?.fin || ''}
              onChange={(e) => setPlanner({ ...planner, fna_numbers: { ...planner.fna_numbers, fin: e.target.value } })}
              data-testid="fin-input"
            />
          </div>
          <div>
            <Label htmlFor="debt-freedom">Debt Freedom Date</Label>
            <Input
              id="debt-freedom"
              type="date"
              value={planner.fna_numbers?.debt_freedom_date || ''}
              onChange={(e) => setPlanner({ ...planner, fna_numbers: { ...planner.fna_numbers, debt_freedom_date: e.target.value } })}
              data-testid="debt-freedom-input"
            />
          </div>
          <div>
            <Label htmlFor="income-protection">Income Protection Number</Label>
            <Input
              id="income-protection"
              type="number"
              placeholder="$"
              value={planner.fna_numbers?.income_protection || ''}
              onChange={(e) => setPlanner({ ...planner, fna_numbers: { ...planner.fna_numbers, income_protection: e.target.value } })}
              data-testid="income-protection-input"
            />
          </div>
        </div>
      </Card>

      {/* Section D: Sprint to District Tracker */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-[#E31837] mb-4">Sprint to District Tracker</h2>
        {sprintFilled && (
          <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-600 rounded">
            <p className="text-green-800 font-bold">🎉 $600 Fast Start Bonus Unlocked!</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-semibold mb-3">3 Recruits</h3>
            <div className="space-y-2">
              {[1, 2, 3].map(num => {
                const recruitName = planner.sprint_tracker?.[`recruit${num}`] || '';
                const prospect = findProspectByName(recruitName);
                return (
                  <div key={num} className="flex items-center gap-2">
                    <Input
                      placeholder={`Recruit #${num} Name`}
                      value={recruitName}
                      onChange={(e) => setPlanner({ ...planner, sprint_tracker: { ...planner.sprint_tracker, [`recruit${num}`]: e.target.value } })}
                      data-testid={`sprint-recruit-${num}-input`}
                      className="flex-1"
                    />
                    {recruitName && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => navigateToProspect(recruitName)}
                        className={`p-2 ${prospect ? 'text-[#E31837] hover:text-[#C4122C]' : 'text-gray-400'}`}
                        title={prospect ? 'View in Pipeline' : 'Add to Pipeline'}
                        data-testid={`recruit-link-${num}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-3">3 Life Apps</h3>
            <div className="space-y-2">
              {[1, 2, 3].map(num => (
                <Input
                  key={num}
                  placeholder={`Client #${num} Name`}
                  value={planner.sprint_tracker?.[`app${num}`] || ''}
                  onChange={(e) => setPlanner({ ...planner, sprint_tracker: { ...planner.sprint_tracker, [`app${num}`]: e.target.value } })}
                  data-testid={`sprint-app-${num}-input`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Appointment Setting Scripts - Integrated from Playbook */}
        <Separator className="my-6" />
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-200">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-[#E31837]" />
            <h3 className="font-semibold text-[#333333]">Appointment Setting Scripts</h3>
            <Badge variant="outline" className="text-xs">From Playbook</Badge>
          </div>
          <p className="text-sm text-gray-600 mb-4">Use these scripts to book appointments with your recruits and prospects.</p>
          
          <div className="space-y-4">
            {appointmentScripts.map((script) => (
              <div key={script.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-[#333333] text-sm">{script.title}</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(script.content);
                      toast.success('Script copied to clipboard!');
                    }}
                    className="flex items-center gap-1 text-xs"
                    data-testid={`copy-script-${script.id}`}
                  >
                    <Copy className="h-3 w-3" />
                    Copy
                  </Button>
                </div>
                <p className="text-sm text-gray-700 mb-2 italic bg-gray-50 p-3 rounded border-l-4 border-l-[#E31837]">
                  &ldquo;{script.content}&rdquo;
                </p>
                <p className="text-xs text-blue-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {script.tip}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* License Checkboxes */}
        <Separator className="my-6" />
        <h3 className="font-semibold mb-4 text-[#333333]">License Requirements</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Checkbox
              id="llqp-license"
              checked={planner.llqp_license || false}
              onCheckedChange={(checked) => setPlanner({ ...planner, llqp_license: checked })}
              data-testid="llqp-checkbox"
            />
            <label htmlFor="llqp-license" className="text-sm font-medium cursor-pointer flex-1">
              LLQP License Passed ✓
            </label>
            {planner.llqp_license && (
              <Badge className="bg-green-600">Completed</Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <Checkbox
              id="mutual-fund"
              checked={planner.mutual_fund_exam || false}
              onCheckedChange={(checked) => setPlanner({ ...planner, mutual_fund_exam: checked })}
              data-testid="mutual-fund-checkbox"
            />
            <label htmlFor="mutual-fund" className="text-sm font-medium cursor-pointer flex-1">
              Mutual Fund Exam Passed ✓
            </label>
            {planner.mutual_fund_exam && (
              <Badge className="bg-green-600">Completed</Badge>
            )}
          </div>
        </div>

        {sprintFilled && planner.llqp_license && (
          <div className="mt-4 p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-600 rounded">
            <p className="text-yellow-900 font-bold flex items-center gap-2">
              <Award className="h-5 w-5" />
              Ready for District Leader Promotion! 🎯
            </p>
          </div>
        )}
      </Card>

      {/* Section E: Top 10 Contact List */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-[#E31837] mb-4">Top 10 Contact List</h2>
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold mb-3 text-[#333333]">My 5 Favourite People</h3>
            <div className="space-y-3">
              {[0, 1, 2, 3, 4].map(idx => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input
                    placeholder="Name"
                    value={getTopContact(idx, 'name')}
                    onChange={(e) => updateTopContact(idx, 'name', e.target.value)}
                    data-testid={`top-contact-name-${idx}`}
                  />
                  <Input
                    placeholder="Phone"
                    value={getTopContact(idx, 'phone')}
                    onChange={(e) => updateTopContact(idx, 'phone', e.target.value)}
                    data-testid={`top-contact-phone-${idx}`}
                  />
                  <Input
                    placeholder="Notes"
                    value={getTopContact(idx, 'notes')}
                    onChange={(e) => updateTopContact(idx, 'notes', e.target.value)}
                    data-testid={`top-contact-notes-${idx}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <Separator />
          <div>
            <h3 className="font-semibold mb-3 text-[#333333]">5 People for Whom I'm Their Favourite</h3>
            <div className="space-y-3">
              {[5, 6, 7, 8, 9].map(idx => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <Input
                    placeholder="Name"
                    value={getTopContact(idx, 'name')}
                    onChange={(e) => updateTopContact(idx, 'name', e.target.value)}
                    data-testid={`top-contact-name-${idx}`}
                  />
                  <Input
                    placeholder="Phone"
                    value={getTopContact(idx, 'phone')}
                    onChange={(e) => updateTopContact(idx, 'phone', e.target.value)}
                    data-testid={`top-contact-phone-${idx}`}
                  />
                  <Input
                    placeholder="Notes"
                    value={getTopContact(idx, 'notes')}
                    onChange={(e) => updateTopContact(idx, 'notes', e.target.value)}
                    data-testid={`top-contact-notes-${idx}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Section F: Path to Greatness */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-[#E31837] mb-4">Path to Greatness Goal Dates</h2>
        <div className="space-y-4">
          {milestones.map((milestone, idx) => {
            const Icon = milestone.icon;
            return (
              <div key={milestone.id} className="border-l-4 border-l-[#E31837] pl-4 py-2">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-5 w-5 text-[#E31837]" />
                  <h4 className="font-semibold text-[#333333]">{milestone.label}</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Target Date</Label>
                    <Input
                      type="date"
                      value={getMilestoneDate(milestone.id, 'target_date')}
                      onChange={(e) => updateMilestone(milestone.id, 'target_date', e.target.value)}
                      data-testid={`milestone-${milestone.id}-target`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Achieved Date</Label>
                    <Input
                      type="date"
                      value={getMilestoneDate(milestone.id, 'achieved_date')}
                      onChange={(e) => updateMilestone(milestone.id, 'achieved_date', e.target.value)}
                      data-testid={`milestone-${milestone.id}-achieved`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Section G: Approach Scripts */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-6 w-6 text-[#E31837]" />
          <h2 className="text-xl font-bold text-[#E31837]">Approach Scripts (Reference)</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-2">
          {scripts.map((script, idx) => (
            <AccordionItem key={idx} value={`script-${idx}`} className="border rounded-lg px-4">
              <AccordionTrigger className="hover:no-underline">
                <span className="font-semibold text-[#333333]">{script.title}</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-700 pt-2">
                {script.content}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Card>

      {/* Section H: Terminology Glossary */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-6 w-6 text-[#E31837]" />
          <h2 className="text-xl font-bold text-[#E31837]">Terminology Glossary</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {terminology.map((item, idx) => (
            <div key={idx} className="p-4 bg-[#F8F9FA] rounded-lg border border-gray-200">
              <h4 className="font-bold text-[#333333] mb-1">{item.term}</h4>
              <p className="text-sm text-gray-600">{item.definition}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
